import React, { useState, useEffect, useRef } from 'react';
import { GameStatus, QuizQuestion } from './types';
import { STATIC_QUIZZES } from './constants';
import { generateQuizQuestions, getQuizCommentary } from './services/quizService';
import { gameService } from '../../../services/gameService';
import { footyMasterService } from '../../../services/footyMasterService';
import { pointService } from '../../../services/pointService';
import { useLanguage } from '../../../context/LanguageContext';
import { useAuth } from '../../../context/AuthContext';
import { useToast } from '../../../context/ToastContext';
import { Trophy, Clock, CheckCircle2, XCircle, Brain, Target, ChevronRight, Sparkles, Star } from 'lucide-react';
import { t, tr } from '../../../i18n';
import SEO from '../../Common/SEO';
import { ConfirmModal } from '../../Common/ConfirmModal';

export const FootyMasterGameView: React.FC = () => {
  const { user, isLoading: isAuthLoading } = useAuth();
  const { lang } = useLanguage();
  const { showToast } = useToast();
  const [status, setStatus] = useState<GameStatus>(GameStatus.START);
  const [questions, setQuestions] = useState<QuizQuestion[]>([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [score, setScore] = useState(0);
  const [correctCount, setCorrectCount] = useState(0);
  const [timeLeft, setTimeLeft] = useState(10);
  const [selectedOption, setSelectedOption] = useState<number | null>(null);
  const [isAnswered, setIsAnswered] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [commentary, setCommentary] = useState('');
  const [highScore, setHighScore] = useState(0);
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);

  const timerRef = useRef<number | null>(null);

  useEffect(() => {
    const saved = localStorage.getItem('footy-master-highscore');
    if (saved) setHighScore(parseInt(saved));
  }, []);

  // Timer logic
  useEffect(() => {
    if (status === GameStatus.PLAYING && !isAnswered) {
      if (timeLeft > 0) {
        timerRef.current = window.setTimeout(() => setTimeLeft(timeLeft - 1), 1000);
      } else {
        handleAnswer(-1); // Time out
      }
    }
    return () => { if (timerRef.current) window.clearTimeout(timerRef.current); };
  }, [status, timeLeft, isAnswered]);

  const startGame = async () => {
    if (!user) {
      setIsLoginModalOpen(true);
      return;
    }

    if (user) {
      const deductionResult = await pointService.deductPoints(user.id, 10, 'Footy Master Entry Fee');
      if (!deductionResult.success) {
        showToast('error', t(lang, 'neon_pitch_insufficient_points'));
        return;
      }
    }

    showToast('info', t(lang, 'neon_pitch_deduction_toast'));
    setIsLoading(true);
    
    // Try to get DB questions first
    let fetchedQuestions: QuizQuestion[] = [];
    try {
        // Fetch 10 random quizzes from all categories
        const dbQuizzes = await footyMasterService.getRandomQuizzes(undefined, 10);
        if (dbQuizzes && dbQuizzes.length > 0) {
            fetchedQuestions = dbQuizzes.map(q => ({
                id: q.id,
                category: q.category,
                question: q[`question_${lang}` as keyof typeof q] as string || q.question_ko,
                options: q[`options_${lang}` as keyof typeof q] as string[] || q.options_ko,
                correctAnswer: q.correct_answer,
                explanation: q[`explanation_${lang}` as keyof typeof q] as string || q.explanation_ko || ''
            }));
        }
    } catch (e) {
        console.error("DB Quiz fetch failed:", e);
    }

    // Fallback to static if DB is empty (AI generation skipped for gameplay performance)
    if (fetchedQuestions.length === 0) {
      // Collect all static quizzes across all categories to have more questions
      fetchedQuestions = Object.values(STATIC_QUIZZES).flat();
    }
    
    // Randomly pick 10 (or less if not available) and shuffle options for each question
    const shuffled = [...fetchedQuestions].sort(() => 0.5 - Math.random());
    const finalQuestions = shuffled.slice(0, 10).map(q => {
      const options = [...q.options];
      const correctAnswerText = options[q.correctAnswer];
      
      // Shuffle options
      for (let i = options.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [options[i], options[j]] = [options[j], options[i]];
      }
      
      // Find new correct answer index
      const newCorrectAnswerIndex = options.indexOf(correctAnswerText);
      
      return {
        ...q,
        options,
        correctAnswer: newCorrectAnswerIndex
      };
    });

    setQuestions(finalQuestions);
    setScore(0);
    setCorrectCount(0);
    setCurrentQuestionIndex(0);
    setTimeLeft(10);
    setIsLoading(false);
    
    // Delay game start slightly to allow toast notification to be seen
    setTimeout(() => {
      setStatus(GameStatus.PLAYING);
    }, 1500);
  };

  const handleAnswer = (optionIndex: number) => {
    if (isAnswered) return;
    
    setSelectedOption(optionIndex);
    setIsAnswered(true);
    
    const currentQuestion = questions[currentQuestionIndex];
    if (optionIndex === currentQuestion.correctAnswer) {
      const bonus = Math.floor(timeLeft * 10);
      const points = 100 + bonus;
      setScore(prev => prev + points);
      setCorrectCount(prev => prev + 1);
    }

    setTimeout(() => {
      nextQuestion();
    }, 2600);
  };

  const nextQuestion = () => {
    if (currentQuestionIndex + 1 < questions.length) {
      setCurrentQuestionIndex(prev => prev + 1);
      setSelectedOption(null);
      setIsAnswered(false);
      setTimeLeft(10);
    } else {
      endGame();
    }
  };

  const restartGame = () => {
    setSelectedOption(null);
    setIsAnswered(false);
    startGame();
  };

  const endGame = async () => {
    setStatus(GameStatus.GAMEOVER);
    const finalCommentary = await getQuizCommentary(score, correctCount, lang);
    setCommentary(finalCommentary);

    if (score > highScore) {
      setHighScore(score);
      localStorage.setItem('footy-master-highscore', score.toString());
    }

    try {
      const games = await gameService.getStoreGames();
      const footyMasterGame = games.find(g => g.title.toUpperCase().includes('FOOTY MASTER'));
      if (footyMasterGame) {
        await gameService.saveScore(footyMasterGame.id, score);
      }
    } catch (error) {
      console.error('Failed to save score:', error);
    }
  };

  if (isAuthLoading) {
    return (
      <div className="min-h-screen bg-[#FFFDF0] flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-16 h-16 border-4 border-yellow-200 border-t-yellow-500 rounded-full animate-spin"></div>
          <p className="text-yellow-600 font-black animate-pulse uppercase tracking-widest text-sm">{t(lang, 'footy_master_preparing_arena')}</p>
        </div>
      </div>
    );
  }

  const handleLoginRedirect = () => {
    setIsLoginModalOpen(false);
    if (window.navigateTo) window.navigateTo('/login');
  };

  return (
    <div className="min-h-screen bg-[#FFFDF0] text-gray-900 font-sans overflow-y-auto custom-scrollbar">
      <SEO 
        title={t(lang, 'footy_master_seo_title')}
        description={t(lang, 'footy_master_seo_description')}
        keywords={t(lang, 'footy_master_seo_keywords')}
      />
      {/* Background Decor - Quiz Theme */}
      <div className="fixed inset-0 pointer-events-none opacity-60">
        <div className="absolute top-[10%] left-[5%] w-24 h-24 bg-yellow-200 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-[20%] right-[10%] w-32 h-32 bg-blue-100 rounded-full blur-3xl animate-pulse" />
        <div className="absolute top-[40%] right-[5%] w-20 h-20 bg-pink-100 rounded-full blur-3xl animate-pulse" />
      </div>

      <div className="relative z-10 max-w-2xl mx-auto px-4 py-4 min-h-screen flex flex-col pb-32">
        
        {/* Header - More Playful */}
        <div className="flex justify-between items-center mb-6 p-3">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-yellow-400 rounded-2xl flex items-center justify-center shadow-lg shadow-yellow-100 rotate-3 hover:rotate-0 transition-transform cursor-pointer">
              <Brain className="w-7 h-7 text-white" />
            </div>
            <div>
              <h1 className="text-xl md:text-2xl font-black tracking-tight text-gray-900 flex items-center gap-1">
                FOOTY<span className="text-blue-500">MASTER</span>
                <Sparkles className="w-4 h-4 text-yellow-500 fill-yellow-500" />
              </h1>
              <p className="text-[10px] text-gray-400 uppercase tracking-widest font-bold">{t(lang, 'footy_master_trivia_quiz')}</p>
            </div>
          </div>
          <div className="text-right px-4 py-2 bg-white rounded-2xl shadow-inner border border-gray-50">
            <p className="text-[9px] text-gray-400 uppercase tracking-widest font-black">{t(lang, 'footy_master_high_score')}</p>
            <p className="text-xl font-black text-amber-500 leading-none mt-1">{highScore.toLocaleString()}</p>
          </div>
        </div>

        {/* Game Content */}
        <div className="flex-1 flex flex-col justify-center">
          
          {status === GameStatus.START && (
            <div className="flex-1 flex flex-col items-center justify-center space-y-10 animate-in fade-in zoom-in duration-500">
              {/* Main Visual */}
              <div className="relative">
                <div className="absolute inset-0 bg-yellow-400 blur-[80px] opacity-30" />
                <div className="relative w-48 h-48 bg-white rounded-[48px] shadow-2xl flex items-center justify-center border-4 border-white transform hover:scale-105 transition-transform duration-300">
                  <div className="absolute -top-6 -left-6 w-14 h-14 bg-blue-500 rounded-2xl flex items-center justify-center shadow-xl -rotate-12">
                    <Star className="w-7 h-7 text-white fill-white" />
                  </div>
                  <Trophy className="w-28 h-28 text-yellow-400 drop-shadow-xl animate-bounce-slow" />
                  <div className="absolute -bottom-4 -right-4 w-14 h-14 bg-pink-500 rounded-2xl flex items-center justify-center shadow-xl rotate-12">
                    <Target className="w-7 h-7 text-white" />
                  </div>
                </div>
              </div>

              {/* Text Info */}
              <div className="text-center space-y-4 max-w-sm">
                <h2 className="text-4xl font-black tracking-tight text-gray-900">
                  QUIZ <span className="text-blue-500 italic">TIME!</span>
                </h2>
                <p className="text-gray-500 font-bold leading-relaxed px-4">
                  {t(lang, 'footy_master_start_desc')}
                </p>
              </div>

              {/* Actions */}
              <div className="w-full max-w-md space-y-4 pt-4">
                <button 
                  onClick={startGame}
                  disabled={isLoading}
                  className="group relative w-full py-6 bg-yellow-400 text-yellow-900 rounded-[28px] font-black text-2xl overflow-hidden transition-all hover:bg-yellow-500 hover:shadow-2xl hover:shadow-yellow-200 active:scale-[0.96] flex items-center justify-center gap-3 border-b-8 border-yellow-600 disabled:opacity-70 disabled:cursor-not-allowed"
                >
                  {isLoading ? (
                    <div className="flex items-center gap-3">
                      <div className="w-6 h-6 border-3 border-yellow-900/30 border-t-yellow-900 rounded-full animate-spin" />
                      <span>{t(lang, 'footy_master_preparing_arena')}</span>
                    </div>
                  ) : (
                    <>
                      {t(lang, 'footy_master_get_started')}
                      <ChevronRight className="w-8 h-8 group-hover:translate-x-1 transition-transform" />
                    </>
                  )}
                </button>
                <button 
                  onClick={() => window.navigateTo('/gamestore')}
                  className="w-full py-4 text-gray-400 hover:text-gray-600 text-sm font-black tracking-widest uppercase transition-colors flex items-center justify-center gap-2"
                >
                  {t(lang, 'footy_master_back_to_store')}
                </button>
              </div>
            </div>
          )}

          {status === GameStatus.PLAYING && questions.length > 0 && (
            <div className="space-y-6 animate-in fade-in duration-300">
              {/* Progress & Info */}
              <div className="flex justify-between items-center px-2">
                <div className="space-y-2">
                  <p className="text-[10px] text-gray-400 uppercase tracking-widest font-black flex items-center gap-2">
                    <Star className="w-3 h-3 text-yellow-500 fill-yellow-500" />
                    {tr(lang, 'footy_master_question_n_of_m', { n: currentQuestionIndex + 1, m: questions.length })}
                  </p>
                  <div className="flex gap-1.5 bg-white/50 p-1.5 rounded-full border border-white/80">
                    {questions.map((_, i) => (
                      <div 
                        key={i} 
                        className={`h-2 w-4 rounded-full transition-all duration-300 ${i < currentQuestionIndex ? 'bg-blue-500' : i === currentQuestionIndex ? 'bg-yellow-400 scale-125 shadow-sm' : 'bg-gray-200'}`} 
                      />
                    ))}
                  </div>
                </div>
                <div className="bg-white px-5 py-3 rounded-2xl shadow-sm border border-gray-50 text-center min-w-[100px]">
                  <p className="text-[9px] text-gray-400 uppercase tracking-widest font-black leading-none mb-1">Score</p>
                  <p className="text-2xl font-black text-blue-500 leading-none">{score.toLocaleString()}</p>
                </div>
              </div>

              {/* Explanation - Moved to top */}
              {isAnswered && questions[currentQuestionIndex].explanation && (
                <div className="animate-in slide-in-from-top-4 fade-in duration-300">
                  <div className="bg-blue-500 text-white rounded-3xl p-6 text-sm font-bold shadow-xl shadow-blue-100 relative">
                    <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 w-4 h-4 bg-blue-500 rotate-45" />
                    <div className="flex gap-3 items-start">
                      <Sparkles className="w-5 h-5 shrink-0 mt-0.5 fill-yellow-300 text-yellow-300" />
                      <p className="leading-relaxed italic">{questions[currentQuestionIndex].explanation}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Question Card */}
              <div className="bg-white border-4 border-white rounded-[40px] p-4 md:p-6 space-y-4 shadow-2xl shadow-yellow-100/50 relative overflow-hidden">
                <div className="absolute top-0 right-0 w-32 h-32 bg-yellow-50 rounded-full -mr-16 -mt-16 opacity-50" />
                
                <div className="flex justify-center relative z-10">
                  <div className={`w-20 h-20 rounded-full border-4 flex flex-col items-center justify-center font-black transition-all ${timeLeft <= 5 ? 'border-pink-500 text-pink-500 animate-bounce' : 'border-blue-400 text-blue-500'} bg-white shadow-lg`}>
                    <span className="text-[9px] uppercase tracking-tighter leading-none mb-0.5">Time</span>
                    <span className="text-2xl leading-none">{timeLeft}</span>
                  </div>
                </div>

                <h3 className="text-xl md:text-2xl font-black text-center leading-tight text-gray-800 relative z-10">
                  {questions[currentQuestionIndex].question}
                </h3>

                <div className="grid grid-cols-1 gap-4 relative z-10">
                  {questions[currentQuestionIndex].options.map((option, idx) => {
                    const isCorrect = idx === questions[currentQuestionIndex].correctAnswer;
                    const isSelected = idx === selectedOption;
                    
                    let bgClass = "bg-[#F8F9FF] border-white text-gray-700";
                    if (isAnswered) {
                      if (isCorrect) bgClass = "bg-green-500 border-green-600 text-white shadow-lg scale-[1.02]";
                      else if (isSelected) bgClass = "bg-pink-500 border-pink-600 text-white shadow-lg";
                      else bgClass = "bg-gray-50 border-transparent opacity-40";
                    }

                    return (
                      <button
                        key={idx}
                        disabled={isAnswered}
                        onClick={() => handleAnswer(idx)}
                        className={`flex items-center gap-4 p-4 rounded-[24px] border-b-4 transition-all font-black text-left group ${bgClass}`}
                      >
                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center text-sm border-2 transition-colors ${isAnswered && isCorrect ? 'bg-white border-white text-green-500' : 'bg-white border-gray-100 text-blue-400'}`}>
                          {String.fromCharCode(65 + idx)}
                        </div>
                        <span className="flex-1">{option}</span>
                        {isAnswered && isCorrect && <CheckCircle2 className="w-6 h-6 text-white" />}
                        {isAnswered && isSelected && !isCorrect && <XCircle className="w-6 h-6 text-white" />}
                      </button>
                    );
                  })}
                </div>
              </div>

            </div>
          )}

          {status === GameStatus.GAMEOVER && (
            <div className="text-center space-y-8 animate-in fade-in zoom-in duration-600">
              <div className="space-y-4">
                <div className="w-28 h-28 bg-white rounded-[40px] shadow-2xl flex items-center justify-center mx-auto border-4 border-yellow-400 relative">
                  <Trophy className="w-14 h-14 text-yellow-400" />
                  <div className="absolute -top-2 -right-2 w-10 h-10 bg-pink-500 rounded-full flex items-center justify-center shadow-lg animate-bounce">
                    <Star className="w-6 h-6 text-white fill-white" />
                  </div>
                </div>
                <h2 className="text-4xl font-black text-gray-900 uppercase tracking-tighter">MISSION <span className="text-blue-500">COMPLETE!</span></h2>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="bg-white p-6 rounded-[32px] border-b-8 border-gray-100 shadow-xl">
                  <p className="text-[10px] text-gray-400 uppercase tracking-widest font-black mb-1">{t(lang, 'footy_master_total_score')}</p>
                  <p className="text-4xl font-black text-gray-900">{score.toLocaleString()}</p>
                </div>
                <div className="bg-white p-6 rounded-[32px] border-b-8 border-gray-100 shadow-xl">
                  <p className="text-[10px] text-gray-400 uppercase tracking-widest font-black mb-1">{t(lang, 'footy_master_accuracy')}</p>
                  <p className="text-4xl font-black text-blue-500">{correctCount}/10</p>
                </div>
              </div>
              
              {/* 
              {commentary && (
                <div className="bg-yellow-400 text-yellow-900 rounded-[32px] p-8 font-black text-xl shadow-2xl shadow-yellow-100 border-b-8 border-yellow-600 italic leading-relaxed">
                  "{commentary}"
                </div>
              )} */}

              <div className="flex flex-col gap-4 pt-4">
                <button 
                  onClick={restartGame}
                  className="w-full py-6 bg-blue-500 text-white rounded-[28px] font-black text-xl hover:bg-blue-600 transition-all shadow-xl shadow-blue-100 border-b-8 border-blue-700 active:scale-[0.98]"
                >
                  {t(lang, 'footy_master_play_again')}
                </button>
                <button 
                  onClick={() => window.navigateTo('/gamestore')}
                  className="w-full py-5 bg-white text-gray-500 border border-gray-100 rounded-[28px] font-black text-lg hover:bg-gray-50 transition-all shadow-sm active:scale-[0.98]"
                >
                  {t(lang, 'footy_master_exit_to_store')}
                </button>
              </div>
            </div>
          )}

        </div>
      </div>

      <ConfirmModal
        isOpen={isLoginModalOpen}
        onClose={() => setIsLoginModalOpen(false)}
        onConfirm={handleLoginRedirect}
        title={t(lang, 'loginRequired')}
        description={t(lang, 'loginParticipate')}
        confirmLabel={t(lang, 'signInLabel')}
        cancelLabel={t(lang, 'cancel')}
      />
    </div>
  );
};
